import {
  IXC_ASSETS_V1_SYMBOL_SUFFIX_INFO_EXTENSION_SUFFIX_MAP,
  type IxcAssetsV1AssetType,
  type IxcAssetsV1AssetTypeEquity,
  type IxcAssetsV1AssetTypeOneOfValue,
  type IxcAssetsV1Controls,
  type IxcAssetsV1MoneyMarket,
  type IxcAssetsV1Symbol,
} from '@interxfi/types';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';

export const ASSET_SECURITY_TYPES = ['equity', 'moneyMarket'] as const satisfies IxcAssetsV1AssetTypeOneOfValue[];

export function getIsAssetHomeCurrency(data: IxcAssetsV1AssetType | undefined) {
  return Boolean(data && 'cash' in data && Boolean(data.cash.homeCurrency));
}

export function getIsAssetSecurity(data: IxcAssetsV1AssetType | undefined) {
  return Boolean(data && ASSET_SECURITY_TYPES.some((type) => type in data));
}

export function getIsAssetEquity(data: IxcAssetsV1AssetType | undefined) {
  return Boolean(data && 'equity' in data);
}

export function formatAssetSymbol(data: IxcAssetsV1Symbol | undefined): string | undefined {
  if (!data) {
    return undefined;
  }

  const parts: string[] = [];

  if (data.base) {
    parts.push(data.base);
  }

  if (data.suffix && data.suffix in IXC_ASSETS_V1_SYMBOL_SUFFIX_INFO_EXTENSION_SUFFIX_MAP) {
    parts.push(
      IXC_ASSETS_V1_SYMBOL_SUFFIX_INFO_EXTENSION_SUFFIX_MAP[data.suffix as keyof typeof IXC_ASSETS_V1_SYMBOL_SUFFIX_INFO_EXTENSION_SUFFIX_MAP],
    );
  }

  return parts.filter(Boolean).join('') || undefined;
}

export function getAssetCusip(data: IxcAssetsV1AssetType | undefined): string | undefined {
  if (!data) {
    return undefined;
  }

  if ('cash' in data) {
    return undefined;
  }

  if ('equity' in data) {
    return data.equity.cusip;
  }

  if ('moneyMarket' in data) {
    return data.moneyMarket.cusip;
  }

  exhaustiveCheck(data['!type']);

  return undefined;
}

export function getAssetSymbol(data: IxcAssetsV1AssetType | undefined): string | undefined {
  if (!data) {
    return undefined;
  }

  if ('cash' in data) {
    return data.cash.currency;
  }

  if ('equity' in data) {
    return formatAssetSymbol(data.equity.symbol);
  }

  if ('moneyMarket' in data) {
    return formatAssetSymbol(data.moneyMarket.symbol);
  }

  exhaustiveCheck(data['!type']);

  return undefined;
}

export function getAssetName(data: IxcAssetsV1AssetType | undefined, backupToSymbol = true): string | undefined {
  if (!data) {
    return undefined;
  }

  if ('cash' in data) {
    return data.cash.currency;
  }

  if ('equity' in data) {
    return data.equity.name || (backupToSymbol ? getAssetSymbol(data) : undefined);
  }

  if ('moneyMarket' in data) {
    return data.moneyMarket.name || (backupToSymbol ? getAssetSymbol(data) : undefined);
  }

  exhaustiveCheck(data['!type']);

  return undefined;
}

export function getAssetControls(data: IxcAssetsV1AssetType | undefined): IxcAssetsV1Controls | undefined {
  if (!data) {
    return undefined;
  }

  if ('cash' in data) {
    return undefined;
  }

  if ('equity' in data) {
    return data.equity.controls;
  }

  if ('moneyMarket' in data) {
    return data.moneyMarket.controls;
  }

  exhaustiveCheck(data['!type']);

  return undefined;
}

export function formatSecurityNamePrioritizeCusip(security: IxcAssetsV1AssetTypeEquity | IxcAssetsV1MoneyMarket | undefined) {
  const parts: string[] = [];

  if (!security) {
    return undefined;
  }

  if (security.cusip) {
    parts.push(security.cusip);
  }

  if (security.name) {
    parts.push(security.name);
  }

  return parts.join(':');
}

export function formatAssetNamePrioritizeCusip(asset: IxcAssetsV1AssetType | undefined) {
  if (!asset) {
    return undefined;
  }

  if ('cash' in asset) {
    return asset.cash.currency || 'Cash';
  }

  if ('equity' in asset) {
    return formatSecurityNamePrioritizeCusip(asset.equity);
  }

  if ('moneyMarket' in asset) {
    return formatSecurityNamePrioritizeCusip(asset.moneyMarket);
  }

  exhaustiveCheck(asset['!type']);

  return undefined;
}

export function getAssetDisplayName(asset: IxcAssetsV1AssetType | undefined) {
  if (!asset) {
    return undefined;
  }

  if ('cash' in asset) {
    return asset.cash.currency || 'Cash';
  }

  if ('equity' in asset) {
    const parts: string[] = [];

    if (asset.equity.name) {
      parts.push(asset.equity.name);
    }

    const formattedSymbol = formatAssetSymbol(asset.equity.symbol);
    if (formattedSymbol) {
      parts.push(parts.length ? `(${formattedSymbol})` : formattedSymbol);
    }

    if (!parts.length) {
      return asset.equity.cusip;
    }

    return parts.join(' ');
  }

  if ('moneyMarket' in asset) {
    const parts: string[] = [];

    if (asset.moneyMarket.name) {
      parts.push(asset.moneyMarket.name);
    }

    const formattedSymbol = formatAssetSymbol(asset.moneyMarket.symbol);
    if (formattedSymbol) {
      parts.push(parts.length ? `(${formattedSymbol})` : formattedSymbol);
    }

    if (!parts.length) {
      return asset.moneyMarket.cusip;
    }

    return parts.join(' ');
  }

  exhaustiveCheck(asset['!type']);

  return undefined;
}
